import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import useLocalStorage from '../../utils/useLocalStorage';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import localStorageService from '../../utils/localStorageService';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  IdentificationIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  AcademicCapIcon,
  ArrowLeftIcon,
  TagIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const StudentDetail = ({ student }) => {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <div className="flex flex-col items-center mb-6">
        <div className="h-24 w-24 rounded-full bg-forma-500/15 flex items-center justify-center mb-4">
          {student?.photo ? (
            <img src={student.photo} alt={student.name} className="h-24 w-24 rounded-full object-cover" />
          ) : (
            <AcademicCapIcon className="h-12 w-12 text-forma-600 dark:text-forma-400" />
          )}
        </div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-1">{student?.name || 'Nom non renseigné'}</h2>
        <p className="text-slate-600 dark:text-slate-400 text-sm">{student?.studentCode || 'Code non renseigné'}</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <PhoneIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">Téléphone</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{student?.phone || 'Non renseigné'}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <IdentificationIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">Email</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{student?.email || 'Non renseigné'}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <CalendarDaysIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">Date d'inscription</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{student?.joinDate ? new Date(student.joinDate).toLocaleDateString() : 'Non renseigné'}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <CheckCircleIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">Statut</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50 capitalize">{student?.status || 'Inconnu'}</p>
        </div>
      </div>
      
      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Inscriptions actives</h3>
        {(() => {
          const enrollments = localStorageService.getStudentEnrollments(student.id);
          const activeEnrollments = enrollments.filter(e => e.status === 'active');
          
          if (activeEnrollments.length === 0) {
            return <p className="text-slate-600 dark:text-slate-400 italic">Aucune inscription active</p>;
          }
          
          return (
            <div className="space-y-4">
              {activeEnrollments.map(enrollment => {
                const batch = localStorageService.getBatchWithEnrollment(enrollment.batchId);
                if (!batch) return null;
                
                return (
                  <div key={enrollment.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-slate-900 dark:text-slate-50 font-medium">{batch.courseName}</h4>
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                        Actif
                      </span>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">{batch.batchName}</p>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Horaires:</span>
                        <p className="text-slate-900 dark:text-slate-50">{batch.days.join(', ')}</p>
                        <p className="text-slate-900 dark:text-slate-50">{batch.startTime} - {batch.endTime}</p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Période:</span>
                        <p className="text-slate-900 dark:text-slate-50">
                          Du {new Date(batch.startDate).toLocaleDateString()}
                        </p>
                        <p className="text-slate-900 dark:text-slate-50">
                          au {new Date(batch.endDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Formateur:</span>
                        <p className="text-slate-900 dark:text-slate-50">{batch.trainerName}</p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Salle:</span>
                        <p className="text-slate-900 dark:text-slate-50">{batch.room || 'Non définie'}</p>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <span className="text-slate-600 dark:text-slate-400 text-sm">Inscription:</span>
                      <p className="text-slate-900 dark:text-slate-50 text-sm">
                        {new Date(enrollment.enrollmentDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

const StudentForm = ({ isEditing = false, studentData = null }) => {
  const navigate = useNavigate();
  // Utiliser notre hook personnalisé
  const { addItem: addStudent, updateItem: updateStudent } = useLocalStorage('students');
  
  const queryClient = useQueryClient();
  
  // Real-time batches data
  const { data: batchesData = [], refetch: refetchBatches } = useQuery(
    'batches',
    () => {
      // Get all batches with enrollment information
      const batches = localStorageService.getAllBatchesWithEnrollment();
      console.log('📚 Fetched real batches from localStorage:', batches);
      // Ensure we always return an array
      return Array.isArray(batches) ? batches : [];
    },
    {
      staleTime: 1000,
      refetchInterval: 5000,
      refetchOnWindowFocus: true
    }
  );

  // Initialize formData first
  const [formData, setFormData] = useState({
    name: studentData?.name || "",
    email: studentData?.email || "",
    phone: studentData?.phone || "",
    status: studentData?.status || "active",
    studentCode: studentData?.studentCode || `ETU-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    joinDate: studentData?.joinDate || new Date().toISOString().split('T')[0],
    selectedBatch: "", // NEW: Replace course/session with batch selection
    photo: null,
    // Address fields
    address: {
      street: studentData?.address?.street || "",
      city: studentData?.address?.city || "",
      postalCode: studentData?.address?.postalCode || "",
      state: studentData?.address?.state || "",
      country: studentData?.address?.country || "Algérie"
    }
  });

  // Process available batches
  const availableBatches = React.useMemo(() => {
    console.log('🔍 Processing available batches:', batchesData);
    
    // Group batches by course for better organization
    const batchesByCourse = {};
    
    if (Array.isArray(batchesData)) {
      batchesData.forEach(batch => {
        if (batch.status === 'active' && !batch.isFull) {
          if (!batchesByCourse[batch.courseName]) {
            batchesByCourse[batch.courseName] = [];
          }
          batchesByCourse[batch.courseName].push(batch);
        }
      });
    }
    
    console.log('📚 Batches by course:', batchesByCourse);
    return batchesByCourse;
  }, [batchesData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested address fields
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    }
  };

  // Function to update session enrollment in real-time
  const updateSessionEnrollment = async (sessionId, action, studentData) => {
    try {
      const sessions = localStorageService.getAllItems('sessions');
      const sessionIndex = sessions.findIndex(s => s.id === sessionId);
      
      if (sessionIndex !== -1) {
        const session = sessions[sessionIndex];
        const currentEnrollment = session.enrolledStudents || 0;
        const studentsList = session.students || [];
        
        if (action === 'enroll') {
          // Check capacity before enrolling
          if (currentEnrollment < (session.maxStudents || 999)) {
            // Add student to session
            const updatedSession = {
              ...session,
              enrolledStudents: currentEnrollment + 1,
              students: [...studentsList, {
                id: studentData.id,
                name: studentData.name,
                email: studentData.email,
                enrolledAt: new Date().toISOString()
              }]
            };
            
            // Update the sessions array
            sessions[sessionIndex] = updatedSession;
            localStorage.setItem('sessions', JSON.stringify(sessions));
            
            console.log(`✅ Student ${studentData.name} enrolled in session ${sessionId}`);
            
            // Invalidate sessions query to trigger real-time update
            queryClient.invalidateQueries('sessions');
          } else {
            console.warn(`❌ Session ${sessionId} is full (${currentEnrollment}/${session.maxStudents})`);
          }
        } else if (action === 'unenroll') {
          // Remove student from session
          const updatedSession = {
            ...session,
            enrolledStudents: Math.max(0, currentEnrollment - 1),
            students: studentsList.filter(s => s.id !== studentData.id)
          };
          
          // Update the sessions array
          sessions[sessionIndex] = updatedSession;
          localStorage.setItem('sessions', JSON.stringify(sessions));
          
          console.log(`✅ Student ${studentData.name} unenrolled from session ${sessionId}`);
          
          // Invalidate sessions query to trigger real-time update
          queryClient.invalidateQueries('sessions');
        }
      }
    } catch (error) {
      console.error('Error updating session enrollment:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditing && studentData) {
        // Update existing student
        const updatedStudent = {
          ...studentData,
          ...formData
        };
        updateStudent(updatedStudent);
        console.log('Student updated successfully:', updatedStudent);
      } else {
        // Add new student
        const newStudent = {
          ...formData,
          id: `student-${Date.now()}`,
          createdAt: new Date().toISOString()
        };
        
        localStorageService.addItem('students', newStudent);
        
        // Enroll student in selected batch using new enrollment system with capacity check
        if (formData.selectedBatch) {
          // Check batch capacity before enrollment
          const batchWithEnrollment = localStorageService.getBatchWithEnrollment(formData.selectedBatch);
          
          if (!batchWithEnrollment) {
            alert('❌ Erreur: Groupe introuvable');
            return;
          }
          
          if (batchWithEnrollment.isFull) {
            alert(`❌ Impossible d'inscrire l'étudiant!\n\nLe groupe "${batchWithEnrollment.batchName}" est complet.\n\n📊 Capacité: ${batchWithEnrollment.currentEnrollment}/${batchWithEnrollment.maxCapacity} étudiants\n\n💡 Veuillez choisir un autre groupe ou augmenter la capacité.`);
            return;
          }
          
          if (batchWithEnrollment.availableSpots <= 0) {
            alert(`❌ Aucune place disponible!\n\nLe groupe "${batchWithEnrollment.batchName}" n'a plus de places libres.\n\n📊 Places restantes: ${batchWithEnrollment.availableSpots}`);
            return;
          }
          
          try {
            const enrollment = localStorageService.createEnrollment(newStudent.id, formData.selectedBatch);
            console.log('✅ Student enrolled in batch:', enrollment);
            
            // Show success message with capacity info
            const updatedBatch = localStorageService.getBatchWithEnrollment(formData.selectedBatch);
            alert(`✅ Inscription réussie!\n\n👤 Étudiant: ${newStudent.name}\n📚 Groupe: ${updatedBatch.batchName}\n📊 Nouvelle capacité: ${updatedBatch.currentEnrollment}/${updatedBatch.maxCapacity}\n🎯 Places restantes: ${updatedBatch.availableSpots}`);
          } catch (error) {
            console.error('Error creating enrollment:', error);
            alert('❌ Erreur lors de l\'inscription dans le groupe');
            return;
          }
        }
        
        console.log('Student added successfully:', newStudent);
      }
      
      // Invalidate and refetch related queries to update real-time data
      queryClient.invalidateQueries('students');
      queryClient.invalidateQueries('batches');
      queryClient.invalidateQueries('enrollments');
      
      // Navigate back to students list after save
      navigate('/students');
    } catch (error) {
      console.error('Error saving student data:', error);
      alert('Une erreur est survenue lors de l\'enregistrement des données');
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
        {isEditing ? "Modifier l'étudiant" : "Ajouter un étudiant"}
      </h2>
      
      <form className="space-y-8" onSubmit={handleSubmit}>
        {/* Section Informations personnelles */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Informations personnelles
          </h3>
          
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Nom complet *
            </label>
            <input 
              type="text"
              name="name" 
              value={formData.name}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Email *
            </label>
            <input 
              type="email"
              name="email" 
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Téléphone *
            </label>
            <input 
              type="text"
              name="phone" 
              value={formData.phone}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder="+213 XXX XXX XXX"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Statut
            </label>
            <select 
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
            >
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="suspended">Suspendu</option>
            </select>
            </div>
          </div>
        </div>

        {/* Section Adresse */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Adresse
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Rue/Adresse
              </label>
              <input 
                type="text"
                name="address.street" 
                value={formData.address.street}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder="123 Rue de la République"
              />
            </div>
            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Ville
              </label>
              <input 
                type="text"
                name="address.city" 
                value={formData.address.city}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder="Alger"
              />
            </div>
            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Code postal
              </label>
              <input 
                type="text"
                name="address.postalCode" 
                value={formData.address.postalCode}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder="16000"
              />
            </div>
            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Wilaya/État
              </label>
              <input 
                type="text"
                name="address.state" 
                value={formData.address.state}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder="Alger"
              />
            </div>
            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Pays
              </label>
              <input 
                type="text"
                name="address.country" 
                value={formData.address.country}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              />
            </div>
          </div>
        </div>

        {/* Section Formation */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Formation actuelle
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                <TagIcon className="h-4 w-4 inline mr-1" />
                Groupe de cours
              </label>
              <select 
                name="selectedBatch"
                value={formData.selectedBatch}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              >
                <option value="">Sélectionner un groupe</option>
                {Object.entries(availableBatches).map(([courseName, batches]) => (
                  <optgroup key={courseName} label={courseName}>
                    {batches.map(batch => {
                      const batchWithCapacity = localStorageService.getBatchWithEnrollment(batch.id);
                      const isFull = batchWithCapacity?.isFull || false;
                      const availableSpots = batchWithCapacity?.availableSpots || 0;
                      const currentEnrollment = batchWithCapacity?.currentEnrollment || 0;
                      const maxCapacity = batchWithCapacity?.maxCapacity || batch.maxCapacity;
                      
                      return (
                        <option 
                          key={batch.id} 
                          value={batch.id}
                          disabled={isFull}
                          style={isFull ? { color: '#ef4444', fontStyle: 'italic' } : {}}
                        >
                          {batch.batchName} • {batch.days.join(', ')} {batch.startTime}-{batch.endTime} • 
                          {isFull ? ' 🔴 COMPLET' : ` 🟢 ${availableSpots} places`} 
                          ({currentEnrollment}/{maxCapacity})
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
              </select>
              {formData.selectedBatch && (() => {
                const selectedBatch = batchesData.find(b => b.id === formData.selectedBatch);
                return selectedBatch ? (
                  <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div className="text-blue-300 text-sm">
                      <strong>Détails du groupe:</strong>
                      <br />
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <span className="text-blue-200">Cours:</span> {selectedBatch.courseName}
                        </div>
                        <div>
                          <span className="text-blue-200">Formateur:</span> {selectedBatch.trainerName}
                        </div>
                        <div>
                          <span className="text-blue-200">Horaires:</span> {selectedBatch.days.join(', ')}
                        </div>
                        <div>
                          <span className="text-blue-200">Heures:</span> {selectedBatch.startTime} - {selectedBatch.endTime}
                        </div>
                        <div>
                          <span className="text-blue-200">Période:</span> {new Date(selectedBatch.startDate).toLocaleDateString()} au {new Date(selectedBatch.endDate).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="text-blue-200">Places:</span> {selectedBatch.currentEnrollment}/{selectedBatch.maxCapacity} ({selectedBatch.availableSpots} restantes)
                        </div>
                        <div>
                          <span className="text-blue-200">Salle:</span> {selectedBatch.room || 'Non définie'}
                        </div>
                        <div>
                          <span className="text-blue-200">Statut:</span> 
                          {(() => {
                            const batchCapacity = localStorageService.getBatchWithEnrollment(selectedBatch.id);
                            const isFull = batchCapacity?.isFull || false;
                            const availableSpots = batchCapacity?.availableSpots || 0;
                            const enrollmentRate = batchCapacity ? (batchCapacity.currentEnrollment / batchCapacity.maxCapacity) * 100 : 0;
                            
                            let statusColor, statusText, statusIcon;
                            
                            if (isFull) {
                              statusColor = 'bg-red-500/20 text-red-300';
                              statusText = 'COMPLET';
                              statusIcon = '🔴';
                            } else if (enrollmentRate >= 90) {
                              statusColor = 'bg-orange-500/20 text-orange-300';
                              statusText = 'PRESQUE COMPLET';
                              statusIcon = '🟠';
                            } else if (enrollmentRate >= 75) {
                              statusColor = 'bg-yellow-500/20 text-yellow-300';
                              statusText = 'SE REMPLIT';
                              statusIcon = '🟡';
                            } else {
                              statusColor = 'bg-green-500/20 text-green-300';
                              statusText = 'OUVERT';
                              statusIcon = '🟢';
                            }
                            
                            return (
                              <span className={`ml-1 px-2 py-0.5 rounded text-xs ${statusColor}`}>
                                {statusIcon} {statusText} ({availableSpots} places)
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Date d'inscription
              </label>
              <input 
                type="date"
                name="joinDate" 
                value={formData.joinDate}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <Link 
            to="/students"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            Annuler
          </Link>
          <button
            type="submit"
            className={clsx(FORMA_LINK_BTN, 'justify-center')}
          >
            {isEditing ? "Mettre à jour" : "Ajouter"}
          </button>
        </div>
      </form>
    </div>
  );
};

const StudentsPage = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');
  const [studentsWithCourses, setStudentsWithCourses] = useState([]);
  
  // Multi-select state for bulk delete
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  
  // Utiliser notre hook personnalisé pour gérer les étudiants avec localStorage
  const {
    items: students,
    deleteItem: deleteStudent,
    refreshData,
  } = useLocalStorage('students');

  // Load full course data for each student
  useEffect(() => {
    console.log('Processing students with course data...');
    const processedStudents = Array.isArray(students) ? students.map(student => {
      if (student.currentCourse) {
        // If currentCourse is just an ID, load the full course data
        if (typeof student.currentCourse === 'string') {
          const courseData = localStorageService.getItemById('courses', student.currentCourse);
          console.log(`Loading course data for student ${student.name}:`, courseData);
          
          if (courseData) {
            return {
              ...student,
              currentCourse: {
                ...courseData,
                type: courseData.title, // Use course title as type
              }
            };
          } else {
            // Course not found, set to null
            return {
              ...student,
              currentCourse: null
            };
          }
        } else {
          // currentCourse is already a full object
          return student;
        }
      } else {
        // No course assigned
        return student;
      }
    }) : [];
    
    console.log('Processed students with courses:', processedStudents);
    setStudentsWithCourses(processedStudents);
  }, [students]);

  // Statistiques des étudiants (use processed students data)
  const stats = [
    {
      title: 'Étudiants enregistrés',
      value: studentsWithCourses.length.toString(),
      hint: 'Fiche créée dans le système',
      icon: UserGroupIcon,
      iconWrap:
        'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
    },
    {
      title: 'Actifs',
      value: studentsWithCourses.filter((s) => s.status === 'active').length.toString(),
      hint: 'Statut « actif »',
      icon: CheckCircleIcon,
      iconWrap:
        'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
    },
    {
      title: 'Sans formation',
      value: studentsWithCourses.filter((s) => !s.currentCourse).length.toString(),
      hint: 'Aucune formation liée à la fiche',
      icon: XCircleIcon,
      iconWrap: 'bg-rose-100 text-rose-800 shadow-inner dark:bg-rose-950/40 dark:text-rose-400',
    },
  ];

  // Onglets
  const tabs = [
    { id: 'all', name: 'Tous les Étudiants' },
    { id: 'active', name: 'Actifs' },
    { id: 'suspended', name: 'Suspendus' },
    { id: 'inactive', name: 'Inactifs' },
    { id: 'no_course', name: 'Sans Formation' },
  ];

  // Rafraîchir la liste des étudiants lorsque la route change (ex: après ajout/modification)
  useEffect(() => {
    console.log('Location changed, refreshing students data');
    refreshData();
  }, [location, refreshData]);

  const filteredStudents = studentsWithCourses.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.studentCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return student.status === 'active';
    if (activeTab === 'suspended') return student.status === 'suspended';
    if (activeTab === 'inactive') return student.status === 'inactive';
    if (activeTab === 'no_course') return !student.currentCourse;
    return true;
  });

  const tabCounts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const base = studentsWithCourses.filter((student) => {
      if (!q) return true;
      return (
        student.name.toLowerCase().includes(q) ||
        student.studentCode.toLowerCase().includes(q) ||
        student.email.toLowerCase().includes(q)
      );
    });
    return {
      all: base.length,
      active: base.filter((s) => s.status === 'active').length,
      suspended: base.filter((s) => s.status === 'suspended').length,
      inactive: base.filter((s) => s.status === 'inactive').length,
      no_course: base.filter((s) => !s.currentCourse).length,
    };
  }, [studentsWithCourses, searchTerm]);

  const paginatedStudents = filteredStudents.slice((currentPage - 1) * studentsPerPage, currentPage * studentsPerPage);
  const totalStudents = filteredStudents.length;
  const totalPages = Math.ceil(totalStudents / studentsPerPage);
  const pageSliceLabel =
    totalStudents === 0
      ? '—'
      : `${Math.min((currentPage - 1) * studentsPerPage + 1, totalStudents)}–${Math.min(currentPage * studentsPerPage, totalStudents)}`;

  const pageNav = useMemo(
    () => paginationItems(Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1)), Math.max(totalPages, 1)),
    [currentPage, totalPages]
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (studentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet étudiant ?')) {
      try {
        // Utilise la fonction du hook pour supprimer l'étudiant
        const success = deleteStudent(studentId);
        if (!success) {
          throw new Error('Échec de la suppression');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Une erreur est survenue lors de la suppression');
      }
    }
  };

  // Multi-select functions
  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        // Remove from selection
        return prev.filter(id => id !== studentId);
      } else {
        // Add to selection
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      // Deselect all
      setSelectedStudents([]);
      setIsSelectAll(false);
    } else {
      // Select all visible students
      const visibleStudentIds = paginatedStudents.map(s => s.id);
      setSelectedStudents(visibleStudentIds);
      setIsSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Delete selected students using the hook function
      let successCount = 0;
      selectedStudents.forEach(studentId => {
        const success = deleteStudent(studentId);
        if (success) successCount++;
      });
      
      // Clear selection
      setSelectedStudents([]);
      setIsSelectAll(false);
      setShowBulkDeleteModal(false);
      
      if (successCount === selectedStudents.length) {
        // All deletions successful - the hook will refresh the data automatically
      } else {
        throw new Error(`Seulement ${successCount}/${selectedStudents.length} étudiants ont été supprimés`);
      }
    } catch (error) {
      console.error('Error deleting students:', error);
      alert('Erreur lors de la suppression des étudiants');
    }
  };

  // Auto-update select all state based on selected students
  useEffect(() => {
    if (selectedStudents.length === 0) {
      setIsSelectAll(false);
    } else {
      const visibleStudentIds = paginatedStudents.map(s => s.id);
      const allVisibleSelected = visibleStudentIds.every(id => selectedStudents.includes(id));
      setIsSelectAll(allVisibleSelected && visibleStudentIds.length > 0);
    }
  }, [selectedStudents, paginatedStudents]);

  const formaLinkBtn = FORMA_LINK_BTN;

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
          <div className="space-y-6">
            <PageHeader
              title="Étudiants"
              description="Gestion des étudiants et de leurs inscriptions"
              actions={
                <Link to="/students/add" className={formaLinkBtn}>
                  <PlusIcon className="h-5 w-5" />
                  <span>Nouvel Étudiant</span>
                </Link>
              }
            />

            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                      whileHover={{ y: -3 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-4">
                          <div
                            className={clsx(
                              'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Statut
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        Compte après recherche
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 lg:-ms-1">
                      {tabs.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => {
                            setActiveTab(tab.id);
                            setCurrentPage(1);
                          }}
                          className={clsx(
                            'inline-flex items-center rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                            activeTab === tab.id
                              ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                          )}
                        >
                          <span>{tab.name}</span>
                          <span
                            className={clsx(
                              'ml-2 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                              activeTab === tab.id
                                ? 'bg-white/20 text-white'
                                : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                            )}
                          >
                            {tabCounts[tab.id]}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="relative w-full lg:max-w-sm lg:flex-shrink-0">
                    <label htmlFor="student-search" className="sr-only">
                      Rechercher un étudiant
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="student-search"
                      type="search"
                      autoComplete="off"
                      placeholder="Nom, code ou email…"
                      value={searchTerm}
                      onChange={handleSearch}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                        searchTerm.trim() ? 'pr-10' : 'pr-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label="Effacer la recherche"
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {(searchTerm.trim() !== '' || activeTab !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                    Filtres actifs
                  </span>
                  {searchTerm.trim() !== '' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      « {searchTerm.trim()} »
                    </span>
                  ) : null}
                  {activeTab !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {tabs.find((t) => t.id === activeTab)?.name}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setActiveTab('all');
                      setCurrentPage(1);
                    }}
                    className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}
              {selectedStudents.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-forma-200/80 bg-forma-50/90 px-5 py-3 dark:border-forma-900/50 dark:bg-forma-950/25">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {selectedStudents.length} étudiant{selectedStudents.length > 1 ? 's' : ''} sélectionné
                      {selectedStudents.length > 1 ? 's' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedStudents([]);
                        setIsSelectAll(false);
                      }}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      Tout désélectionner
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBulkDeleteModal(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Supprimer ({selectedStudents.length})
                  </button>
                </div>
              )}

              {paginatedStudents.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                    <UserGroupIcon className="h-10 w-10 text-forma-500/70 dark:text-forma-400/80" />
                  </div>
                  <div className="max-w-md space-y-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Aucun étudiant à afficher</h3>
                    <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                      Essayez une autre recherche, élargissez le filtre statut avec « Réinitialiser », ou créez une
                      nouvelle fiche étudiant.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setActiveTab('all');
                        setCurrentPage(1);
                      }}
                      className="mx-auto rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Effacer filtres et recherche
                    </button>
                  </div>
                  <Link to="/students/add" className={formaLinkBtn}>
                    <PlusIcon className="h-5 w-5" />
                    <span>Ajouter un étudiant</span>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-200 bg-gradient-to-r from-white to-slate-50/95 px-6 py-4 dark:border-slate-700 dark:from-slate-900 dark:to-slate-950/90">
                    <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                      Liste des étudiants
                    </h2>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                      <span>
                        <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                          {totalStudents}
                        </span>{' '}
                        résultat{totalStudents > 1 ? 's' : ''}
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span>
                        plage{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">
                          {pageSliceLabel}
                        </span>{' '}
                        sur{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">
                          {totalStudents}
                        </span>
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span className="text-slate-500 dark:text-slate-500">{studentsPerPage} par page</span>
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse text-left">
                      <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 text-[11px] font-bold uppercase tracking-wider text-slate-500 shadow-[0_1px_0_rgba(15,23,42,0.06)] backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-400">
                        <tr>
                          <th scope="col" className="w-12 px-4 py-3.5">
                            <span className="sr-only">Sélection</span>
                            <input
                              type="checkbox"
                              checked={isSelectAll}
                              onChange={handleSelectAll}
                              aria-label="Sélectionner tout sur cette page"
                              className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                            />
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Étudiant
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Contact
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Formation
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Statut
                          </th>
                          <th scope="col" className="px-4 py-3.5 text-right font-bold">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedStudents.map((student, rowIdx) => (
                          <motion.tr
                            key={student.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: Math.min(rowIdx * 0.03, 0.35) }}
                            className={clsx(
                              'border-b border-slate-100 transition-colors dark:border-slate-800/80',
                              rowIdx % 2 === 1 && 'bg-slate-50/45 dark:bg-slate-900/40',
                              'hover:bg-forma-50/50 dark:hover:bg-forma-950/20',
                              selectedStudents.includes(student.id) &&
                                'bg-forma-100/55 ring-1 ring-inset ring-forma-200/80 dark:bg-forma-950/40 dark:ring-forma-800/60'
                            )}
                          >
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <input
                                type="checkbox"
                                checked={selectedStudents.includes(student.id)}
                                onChange={() => handleSelectStudent(student.id)}
                                aria-label={`Sélectionner ${student.name}`}
                                className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                              />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="flex items-center">
                                <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-forma-100 dark:bg-forma-950/50">
                                  {student.photo ? (
                                    <img
                                      src={student.photo}
                                      alt={student.name}
                                      className="h-10 w-10 object-cover"
                                    />
                                  ) : (
                                    <AcademicCapIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                                  )}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                    {student.name}
                                  </div>
                                  <div className="text-xs text-slate-600 dark:text-slate-400">{student.studentCode}</div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="text-sm text-slate-700 dark:text-slate-300">{student.phone}</div>
                              <div className="text-xs text-slate-600 dark:text-slate-400">{student.email}</div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              {(() => {
                                const enrollments = localStorageService.getStudentEnrollments(student.id);
                                const activeEnrollments = enrollments.filter((e) => e.status === 'active');

                                if (activeEnrollments.length === 0) {
                                  return (
                                    <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-800 dark:bg-rose-500/15 dark:text-rose-400">
                                      Sans inscription
                                    </span>
                                  );
                                }

                                if (activeEnrollments.length === 1) {
                                  const batch = localStorageService.getBatchWithEnrollment(activeEnrollments[0].batchId);
                                  if (!batch) {
                                    return (
                                      <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-800 dark:bg-rose-500/15 dark:text-rose-400">
                                        Groupe introuvable
                                      </span>
                                    );
                                  }

                                  return (
                                    <div>
                                      <div className="text-sm text-slate-700 dark:text-slate-300">{batch.courseName}</div>
                                      <div className="text-xs text-slate-600 dark:text-slate-400">
                                        {batch.days.join(', ')} • {batch.startTime}-{batch.endTime}
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div>
                                    <div className="text-sm text-slate-700 dark:text-slate-300">
                                      {activeEnrollments.length} inscriptions
                                    </div>
                                    <div className="text-xs text-slate-600 dark:text-slate-400">Inscriptions multiples</div>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span
                                className={clsx(
                                  'rounded-full px-2.5 py-1 text-xs font-medium',
                                  student.status === 'active' &&
                                    'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400',
                                  student.status === 'suspended' &&
                                    'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400',
                                  student.status !== 'active' &&
                                    student.status !== 'suspended' &&
                                    'bg-slate-100 text-slate-600 dark:bg-slate-600/25 dark:text-slate-400'
                                )}
                              >
                                {student.status === 'active'
                                  ? 'Actif'
                                  : student.status === 'suspended'
                                    ? 'Suspendu'
                                    : 'Inactif'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm">
                              <div className="flex justify-end gap-1.5">
                                <Link
                                  to={`/students/${student.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-forma-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-forma-400"
                                  title="Voir les détails"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Link>
                                <Link
                                  to={`/students/edit/${student.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-amber-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                                  title="Modifier"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(student.id)}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-red-600 shadow-sm transition hover:bg-red-50 dark:border-slate-600 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/30"
                                  title="Supprimer"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalStudents > studentsPerPage && (
                    <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 dark:border-slate-600 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-center text-sm text-slate-600 dark:text-slate-400 sm:text-left">
                        Affichage{' '}
                        <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">
                          {pageSliceLabel}
                        </span>{' '}
                        sur <span className="font-semibold tabular-nums">{totalStudents}</span>
                        {' · '}
                        page <span className="tabular-nums">{currentPage}</span> sur{' '}
                        <span className="tabular-nums">{totalPages}</span>
                      </p>
                      <nav
                        className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end"
                        aria-label="Pagination"
                      >
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                          Précédent
                        </button>
                        <div className="mx-1 flex flex-wrap items-center justify-center gap-1">
                          {pageNav.map((item, idx) =>
                            item.type === 'ellipsis' ? (
                              <span
                                key={`ellipsis-${idx}`}
                                className="px-2 font-medium text-slate-400 dark:text-slate-500"
                                aria-hidden
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item.page}
                                type="button"
                                onClick={() => setCurrentPage(item.page)}
                                className={clsx(
                                  'min-w-[2.5rem] rounded-lg px-2 py-1.5 text-sm font-semibold tabular-nums transition',
                                  item.page === currentPage
                                    ? 'bg-forma-600 text-white shadow-sm'
                                    : 'border border-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                                )}
                              >
                                {item.page}
                              </button>
                            )
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          Suivant
                          <ChevronRightIcon className="h-4 w-4" aria-hidden />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </UiCard>
          </div>
        }
      />
      <Route
        path="/add"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <Link to="/students" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Retour à la liste
              </Link>
            </div>
            <StudentForm />
          </motion.div>
        }
      />
      <Route
        path="/edit/:id"
        element={
          <EditStudentRoute />
        }
      />
      <Route
        path="/:id"
        element={
          <ViewStudentRoute />
        }
      />
      </Routes>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-600 dark:bg-slate-900 w-full max-w-md"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <TrashIcon className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                Confirmer la suppression
              </h3>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Êtes-vous sûr de vouloir supprimer {selectedStudents.length} étudiant{selectedStudents.length > 1 ? 's' : ''} ? 
              Cette action est irréversible.
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

// Route components for edit and view student
const EditStudentRoute = () => {
  const { id } = useParams();
  const { getById, isLoading } = useLocalStorage('students');
  const [student, setStudent] = useState(null);
  
  useEffect(() => {
    const studentData = getById(id);
    setStudent(studentData);
  }, [id, getById]);

  if (isLoading || !student) {
    return <div className="text-slate-900 dark:text-slate-50">Chargement...</div>;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-6">
        <Link to="/students" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour à la liste
        </Link>
      </div>
      <StudentForm isEditing={true} studentData={student} />
    </motion.div>
  );
};

const ViewStudentRoute = () => {
  const { id } = useParams();
  const { getById, isLoading } = useLocalStorage('students');
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    console.log('ViewStudentRoute: Looking for student with ID:', id);
    const studentData = getById(id);
    console.log('ViewStudentRoute: Found student data:', studentData);
    
    if (studentData) {
      // If student has a course assigned, load the full course details
      if (studentData.currentCourse) {
        const courseData = localStorageService.getItemById('courses', studentData.currentCourse);
        console.log('ViewStudentRoute: Found course data:', courseData);
        
        if (courseData) {
          // Merge course data with student data
          const studentWithCourse = {
            ...studentData,
            currentCourse: {
              ...courseData,
              type: courseData.title, // Use course title as type
            }
          };
          setStudent(studentWithCourse);
        } else {
          // Course not found, set student without course
          setStudent({
            ...studentData,
            currentCourse: null
          });
        }
      } else {
        // No course assigned
    setStudent(studentData);
      }
    } else {
      setStudent(null);
    }
    setLoading(false);
  }, [id, getById]);

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-900 dark:text-slate-50">Chargement des détails de l'étudiant...</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-700 dark:text-slate-300">
          <p>Étudiant introuvable (ID: {id})</p>
          <Link to="/students" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
            ← Retour à la liste des étudiants
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-6">
        <Link to="/students" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour à la liste
        </Link>
      </div>
      <StudentDetail student={student} />
    </motion.div>
  );
};

export default StudentsPage; 